/**
 * Whether the working behind a right answer on the answer list is analysed —
 * per kind of attempt, since a school may mark its tests by the sheet and want
 * its assignments analysed.
 *
 * "Mark only": a question the student's answer list (OMR or written) shows
 * correct is marked against the key, and its working is not analysed. Wrong
 * answers are always analysed.
 *
 * The editor of the `correctAnswers` tenant setting, framed by TenantSettingsTab.
 * The setting is overridden whole: saving sets both modalities for this tenant.
 * See docs/FeatureDesigns/assignments-tests/answer-list-marking-target.md §3.
 */
import { useState } from 'react';
import { Button, FormField, Select } from '@shared-ui/components';
import type { CorrectAnswersMode, TenantCorrectAnswers } from '../../../api/tenantsApi';
import type { SettingEditorProps } from './TenantSettingsTab';

type Modality = 'practice' | 'test';

const MODALITIES: Array<{ key: Modality; label: string }> = [
    { key: 'practice', label: 'Assignments' },
    { key: 'test', label: 'Tests' },
];

const MODES: Array<{ value: CorrectAnswersMode; label: string }> = [
    { value: 'analyse', label: 'Analyse the working' },
    { value: 'mark_only', label: 'Mark only' },
];

export function CorrectAnswersEditor({ value, save, saving, actions }: SettingEditorProps<'correctAnswers'>) {
    const [draft, setDraft] = useState<TenantCorrectAnswers>({ byModality: { ...value.byModality } });
    const modeOf = (v: TenantCorrectAnswers, key: Modality) => v.byModality[key] ?? 'analyse';
    const dirty = MODALITIES.some(({ key }) => modeOf(value, key) !== modeOf(draft, key));

    return (
        <div className="tenants-form">
            {MODALITIES.map(({ key, label }) => (
                <FormField key={key} label={label}>
                    <Select
                        aria-label={`Correct answers on the answer list — ${label}`}
                        value={modeOf(draft, key)}
                        onChange={e => setDraft(d => ({ byModality: { ...d.byModality, [key]: e.target.value as CorrectAnswersMode } }))}
                    >
                        {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </Select>
                </FormField>
            ))}
            <div className="tenant-detail-save">
                {dirty && <span className="tenant-detail-note">Unsaved changes</span>}
                {actions}
                <Button onClick={() => save(draft)} loading={saving} disabled={!dirty}>Save</Button>
            </div>
        </div>
    );
}
